import React from 'react';
import {FormField, Game as GameType} from "../../types/games";
import {Language, translations} from "../../translations";
import {useAppDispatch} from "../../app/store";
import {setFormData} from "../../app/slices/game";
import {CSSTransition} from 'react-transition-group';
import {TRANSITION_DURATION} from './Game';
import toast from 'react-hot-toast';
import FormFields from "./FormFields";
import {Modal, Button} from 'react-bootstrap';

type Props = {
    handleStart: () => void;
    gameData: GameType;
    slug: string | undefined;
    lang: Language;
}

const GameStart = ({handleStart, gameData, slug, lang}: Props) => {
    const dispatch = useAppDispatch();
    const [isVisible, setIsVisible] = React.useState(true);
    const transRef = React.useRef<HTMLDivElement | null>(null);
    const LANG = translations[lang];
    const [showTerms, setShowTerms] = React.useState(false);
    const descriptionRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        setIsVisible(true);
        // Open modal if URL hash is #terms
        if (window.location && window.location.hash === '#terms') {
            setShowTerms(true);
        }
    }, []);

    React.useEffect(() => {
        const root = transRef.current;
        if (!root) return;
        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            const anchor = target.closest('a') as HTMLAnchorElement | null;
            if (anchor && anchor.getAttribute('href') === '#terms') {
                e.preventDefault();
                setShowTerms(true);
            }
        };
        root.addEventListener('click', handleClick);
        return () => {
            root.removeEventListener('click', handleClick);
        };
    }, [transRef]);


    let languageOptions;
    if (gameData.languages.length > 0) {
        languageOptions = gameData.languages.map((lang, i) => {
            const selectedClass = lang.game.slug === slug ? 'selected-language' : '';
            return (
                <a className={selectedClass} key={i} href={`/games/${lang.game.slug}`}>{lang.name}</a>
            );
        });
    }

    const isValid = gameData.form.every((field: FormField) => !field.required || (field.required && field.value !== ''));

    const handleGameStart = (e: React.FormEvent) => {
        e.preventDefault();
        if (!isValid) {
            toast.error(LANG.fill_all_fields, {position: 'top-right',});
            return;
        }
        setIsVisible(false);
    }


    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // 'keypress' event misbehaves on mobile so we track 'Enter' key via 'keydown' event
        if (event.key === 'Enter' && isValid) {
            event.preventDefault();
            event.stopPropagation();
            handleGameStart(event);
        }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        dispatch(setFormData({[name]: value}));
    }

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, checked} = e.target;
        dispatch(setFormData({[name]: checked ? '1' : ''}));
    }

    const nextBtn = isValid
        ? (
            <div className="circle-button pulse active" onClick={handleGameStart}>
                <div className="triangle-right"></div>
            </div>
        )
        : (
            <div className="circle-button">
                <div className="triangle-right"></div>
            </div>
        );

    const gameImage = gameData.image ? gameData.image : null;

    return (
        <>
            <CSSTransition nodeRef={transRef}
                           onEnter={() => setIsVisible(true)}
                           onExited={() => handleStart()}
                           in={isVisible}
                           unmountOnExit
                           timeout={TRANSITION_DURATION}
                           classNames="slide-right">
                <div className='game-start-screen' ref={transRef}>
                    <div className='game-start-header'>
                        {gameImage &&
                            <div className='game-start-image'>
                                <img src={gameImage} alt={gameData.name}
                                     className='game-thumbnail img-fluid'/>
                            </div>
                        }
                        <div className='game-start-content'>
                            {languageOptions && languageOptions.length > 1 &&
                                <div className='language-options'>
                                {languageOptions}
                            </div>
                            }
                            <h1>{gameData.name}</h1>
                            <div ref={descriptionRef} dangerouslySetInnerHTML={{__html: gameData.description}}></div>
                        </div>
                    </div>
                    {gameData.form && gameData.form.length > 0 &&
                        <div className='game-start-body'>
                            <form onSubmit={handleGameStart} id='game-start-form'
                                  action='' method='POST'>
                                <FormFields form={gameData.form}
                                            handleCheckboxChange={handleCheckboxChange}
                                            handleInputChange={handleInputChange}
                                            onKeyDown={onKeyDown}/>
                            </form>
                        </div>
                    }
                    <div className='glowing-bar'></div>
                </div>
            </CSSTransition>
            <div className='game-bottom-bar'>
                {nextBtn}
            </div>

            <Modal show={showTerms} onHide={() => setShowTerms(false)} centered>
                <Modal.Header closeButton />
                <Modal.Body>
                    <div dangerouslySetInnerHTML={{ __html: gameData.terms || '' }} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowTerms(false)}>
                        {LANG.close}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default GameStart;
